import { Link, useSearchParams } from "react-router-dom";
import { Settings2 } from "lucide-react";
import {
  Button,
  PageHeader,
  PageHeaderActions,
  PageHeaderDescription,
  PageHeaderHeading,
  PageHeaderTitle,
  PageHeaderTop,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@eduviaos/ui-kit";
import { LearningCertificatesTab } from "@/pages/learning/LearningCertificatesTab";
import { LearningProgressTab } from "@/pages/learning/LearningProgressTab";
import { LearningReadLogsTab } from "@/pages/learning/LearningReadLogsTab";
import { LearningStudyGroupsTab } from "@/pages/learning/LearningStudyGroupsTab";

const LEARNING_TABS = [
  { id: "progress", label: "Progress", testId: "learning-tab-progress" },
  { id: "study-groups", label: "Study groups", testId: "learning-tab-study-groups" },
  { id: "certificates", label: "Certificates", testId: "learning-tab-certificates" },
  { id: "read-logs", label: "Read logs", testId: "learning-tab-read-logs" },
] as const;

type LearningTabId = (typeof LEARNING_TABS)[number]["id"];

function isLearningTab(value: string | null): value is LearningTabId {
  return LEARNING_TABS.some((tab) => tab.id === value);
}

/**
 * SC-21e — IA Learning module: progress rollup, study-group moderation,
 * certificate registry, and reading-log report.
 * The active tab is the `tab` query param so links like `?tab=study-groups` open that section.
 */
export function LearningPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get("tab");
  const tab: LearningTabId = isLearningTab(requested) ? requested : "progress";

  const handleTabChange = (next: string) => {
    if (!isLearningTab(next)) return;
    const params = new URLSearchParams(searchParams);
    if (next === "progress") params.delete("tab");
    else params.set("tab", next);
    setSearchParams(params, { replace: true });
  };

  return (
    <div className="space-y-6" data-testid="learning-page">
      <PageHeader>
        <PageHeaderTop>
          <PageHeaderHeading>
            <PageHeaderTitle>Learning</PageHeaderTitle>
            <PageHeaderDescription>
              See who is using your learner app — plans, due reviews, groups, certificates, and
              reading logs.
            </PageHeaderDescription>
          </PageHeaderHeading>
          <PageHeaderActions>
            <Button variant="outline" size="sm" asChild>
              <Link to="/settings/learner-app" data-testid="learning-link-learner-app">
                <Settings2 aria-hidden="true" className="size-4" />
                Learner app settings
              </Link>
            </Button>
            <Button variant="outline" size="sm" asChild>
              <Link to="/settings/learning-defaults" data-testid="learning-link-defaults">
                <Settings2 aria-hidden="true" className="size-4" />
                Learning defaults
              </Link>
            </Button>
          </PageHeaderActions>
        </PageHeaderTop>
      </PageHeader>

      <Tabs value={tab} onValueChange={handleTabChange} className="w-full">
        <TabsList aria-label="Learning sections">
          {LEARNING_TABS.map((item) => (
            <TabsTrigger key={item.id} value={item.id} data-testid={item.testId}>
              {item.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="progress" className="mt-4">
          <LearningProgressTab />
        </TabsContent>
        <TabsContent value="study-groups" className="mt-4">
          <LearningStudyGroupsTab />
        </TabsContent>
        <TabsContent value="certificates" className="mt-4">
          <LearningCertificatesTab />
        </TabsContent>
        <TabsContent value="read-logs" className="mt-4">
          <LearningReadLogsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
